import Maybe from 'true-myth/maybe';
import { z } from 'zod';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';
import { createComputed } from 'zustand-computed';

import type { ApplicationActions } from '~/shared/context/context';
import type { Skill, SkillShort, SkillType } from '~/shared/data-contract/skill';
import type { LanguageCode } from '~/shared/internationalization';

import type { ChangeSkill } from './change-skill';
import type { FeedbackDialogLabelsStoreState } from './feedback-dialog-store-labels';
import type { FeedbackEmitter } from './feedback-emitter';
import {
    fetchedSkillsBySearchTermEvent,
    fetchSkillsBySearchTermEvent,
    sendChangeSkillEvent,
    sendReportSkillEvent,
    sendRequestSkillEvent,
} from './feedback-emitter';
import type { ReportSkill } from './report-skill';
import type { RequestSkill } from './request-skill';

export type FeedbackStoreDependencies = {
    readonly feedbackEmitter: FeedbackEmitter;
    readonly getFeedbackDialogStoreLabels: () => FeedbackDialogLabelsStoreState;
    readonly mailTo: ApplicationActions['mailTo'];
    readonly selectedLanguage: LanguageCode;
};

export type ComputedFeedbackDialogStore = {
    readonly canSubmit: boolean;
};

export type ValidatedInput = {
    readonly isValid: boolean;
    readonly value: Maybe<string>;
};

export type RequestSkillInput = {
    readonly description?: string;
    readonly title?: string;
    readonly type?: SkillType;
};

export type RequestSkillFormState = {
    readonly description: ValidatedInput;
    readonly title: ValidatedInput;
    readonly type: Maybe<SkillType>;
};

export type ReportSkillInput = {
    readonly description?: string;
    readonly skill?: Skill;
};

export type ReportSkillFormState = {
    readonly description: ValidatedInput;
    readonly skill: Maybe<Skill>;
};

export type ChangeSkillInput = {
    readonly description?: string;
    readonly language?: LanguageCode;
    readonly skill?: Skill;
};

export type ChangeSkillFormState = {
    readonly description: ValidatedInput;
    readonly language: Maybe<LanguageCode>;
    readonly skill: Maybe<Skill>;
};

export type FeedbackInput = {
    readonly change: ChangeSkillInput;
    readonly report: ReportSkillInput;
    readonly request: RequestSkillInput;
};

export type Feedback = {
    readonly change: Maybe<ChangeSkillFormState>;
    readonly report: Maybe<ReportSkillFormState>;
    readonly request: Maybe<RequestSkillFormState>;
};

export type FeedbackType = keyof Feedback;

type SearchInputWithVisibility = {
    readonly skills: Maybe<readonly SkillShort[]>;
    readonly value: Maybe<string>;
};

export type SkillSearchInput = {
    readonly change: SearchInputWithVisibility;
    readonly report: SearchInputWithVisibility;
};

export type SkillSearchInputType = keyof SkillSearchInput;

type FeedbackStateToFormState = {
    readonly change: ChangeSkillFormState;
    readonly report: ReportSkillFormState;
    readonly request: RequestSkillFormState;
};

export type FeedbackDialogStoreState = FeedbackDialogLabelsStoreState & {
    readonly currentFeedbackType: FeedbackType;
    readonly feedback: Feedback;
    readonly feedbackRaw: FeedbackInput;
    readonly isFeedbackActionsVisible: boolean;
    readonly skillSearchInput: SkillSearchInput;
};

export type FeedbackDialogStoreActions = {
    readonly clearFeedback: () => void;
    readonly searchSkillsBySearchTerm: () => void;
    readonly sendFeedback: () => void;
    readonly sendFeedbackToCobrainer: (message: string) => void;
    readonly setFeedbackInformation: (information: ChangeSkillInput | ReportSkillInput | RequestSkillInput) => void;
    readonly setFeedbackType: (type: FeedbackType) => void;
    readonly setSkillSearchInput: (value: Maybe<string>) => void;
    readonly toggleFeedbackActionsVisibility: () => void;
    readonly updateStoreLabelsTranslations: () => void;
};

export type FeedbackDialogStore = FeedbackDialogStoreActions & FeedbackDialogStoreState;
export type FeedbackDialogStoreWithComputedState = ComputedFeedbackDialogStore & FeedbackDialogStore;

const textInputSchema = z.string().min(3);

const feedbackStateToEvent = {
    change: sendChangeSkillEvent,
    report: sendReportSkillEvent,
    request: sendRequestSkillEvent,
} as const;

const transformRequestSkillFormStateToEventPayload = (requestSkill: RequestSkillFormState): Maybe<RequestSkill> => {
    const { description, title, type } = requestSkill;

    if (title.value.isJust && description.value.isJust) {
        return Maybe.just({
            description: description.value.value,
            title: title.value.value,
            type: type.unwrapOr(undefined),
        });
    }

    return Maybe.nothing();
};

const transformRequestSkillToFormState = (requestSkill: RequestSkillInput): RequestSkillFormState => {
    const { description, title, type } = requestSkill;

    return {
        description: {
            isValid: textInputSchema.safeParse(description).success,
            value: Maybe.of(description),
        },
        title: {
            isValid: textInputSchema.safeParse(title).success,
            value: Maybe.of(title),
        },
        type: Maybe.of(type),
    };
};

const transformReportSkillFormStateToEventPayload = (reportSkill: ReportSkillFormState): Maybe<ReportSkill> => {
    const { description, skill } = reportSkill;

    if (description.value.isJust && skill.isJust) {
        return Maybe.just({
            description: description.value.value,
            skillId: skill.value.id,
        });
    }

    return Maybe.nothing();
};

const transformReportSkillToFormState = (reportSkill: ReportSkillInput): ReportSkillFormState => {
    const { description, skill } = reportSkill;

    return {
        description: {
            isValid: textInputSchema.safeParse(description).success,
            value: Maybe.of(description),
        },
        skill: Maybe.of(skill),
    };
};

const transformChangeSkillFormStateToEventPayload = (changeSkill: ChangeSkillFormState): Maybe<ChangeSkill> => {
    const { description, language, skill } = changeSkill;

    if (description.value.isJust && skill.isJust && language.isJust) {
        return Maybe.just({
            description: description.value.value,
            language: language.value,
            skillId: skill.value.id,
        });
    }

    return Maybe.nothing();
};

const transformChangeSkillToFormState = (changeSkill: ChangeSkillInput): ChangeSkillFormState => {
    const { description, language, skill } = changeSkill;

    return {
        description: {
            isValid: textInputSchema.safeParse(description).success,
            value: Maybe.of(description),
        },
        language: Maybe.of(language),
        skill: Maybe.of(skill),
    };
};
const transformFormStateToEventPayload = <T extends FeedbackType>(
    feedbackType: T,
    formState: FeedbackStateToFormState[T],
): Maybe<ChangeSkill> | Maybe<ReportSkill> | Maybe<RequestSkill> => {
    switch (feedbackType) {
        case 'change': {
            return transformChangeSkillFormStateToEventPayload(formState as ChangeSkillFormState);
        }
        case 'report': {
            return transformReportSkillFormStateToEventPayload(formState as ReportSkillFormState);
        }
        case 'request': {
            return transformRequestSkillFormStateToEventPayload(formState as RequestSkillFormState);
        }
    }
};

const transformInputToFormState = <T extends FeedbackType>(
    feedbackType: T,
    input: FeedbackInput[T],
): Maybe<ChangeSkillFormState> | Maybe<ReportSkillFormState> | Maybe<RequestSkillFormState> => {
    switch (feedbackType) {
        case 'change': {
            return Maybe.just(transformChangeSkillToFormState(input as ChangeSkillInput));
        }
        case 'report': {
            return Maybe.just(transformReportSkillToFormState(input as ReportSkillInput));
        }
        case 'request': {
            return Maybe.just(transformRequestSkillToFormState(input as RequestSkillInput));
        }
    }
};

export const createComputedFeedbackDialogStore = (): ((state: FeedbackDialogStore) => ComputedFeedbackDialogStore) => {
    return (state) => {
        const { currentFeedbackType } = state;
        const couldSubmitRequest = state.feedback.request.mapOr(false, ({ description, title }) => {
            return description.isValid && title.isValid;
        });
        const couldSubmitReport = state.feedback.report.mapOr(false, ({ description, skill }) => {
            return description.isValid && skill.isJust;
        });
        const couldSubmitChange = state.feedback.change.mapOr(false, ({ description, language, skill }) => {
            return description.isValid && language.isJust && skill.isJust;
        });

        return {
            canSubmit:
                (currentFeedbackType === 'change' && couldSubmitChange) ||
                (currentFeedbackType === 'report' && couldSubmitReport) ||
                (currentFeedbackType === 'request' && couldSubmitRequest),
        };
    };
};

export const createFeedbackDialogStore = (
    dependencies: FeedbackStoreDependencies,
): StoreApi<FeedbackDialogStoreWithComputedState> => {
    const { feedbackEmitter, getFeedbackDialogStoreLabels, mailTo, selectedLanguage } = dependencies;

    const computed = createComputed(createComputedFeedbackDialogStore());

    const computedStore = computed((set, getState) => {
        feedbackEmitter.on(fetchedSkillsBySearchTermEvent, ({ data: skills }) => {
            const { currentFeedbackType, skillSearchInput } = getState();
            if (currentFeedbackType === 'request') {
                return;
            }

            const skillPayload: SearchInputWithVisibility = {
                skills: Maybe.just(skills),
                value: skillSearchInput[currentFeedbackType].value,
            };

            set({
                skillSearchInput: {
                    ...skillSearchInput,
                    [currentFeedbackType]: skillPayload,
                },
            });
        });

        const initialFeedbackState: {
            feedback: Feedback;
            feedbackRaw: FeedbackInput;
        } = {
            feedback: {
                change: Maybe.just<ChangeSkillFormState>({
                    description: {
                        isValid: false,
                        value: Maybe.nothing(),
                    },
                    language: Maybe.just(selectedLanguage),
                    skill: Maybe.nothing(),
                }),
                report: Maybe.nothing(),
                request: Maybe.nothing(),
            },
            feedbackRaw: {
                change: {
                    description: undefined,
                    language: selectedLanguage,
                    skill: undefined,
                },
                report: {},
                request: {},
            },
        };

        const clearFeedback: FeedbackDialogStoreActions['clearFeedback'] = () => {
            set(initialFeedbackState);
        };

        return {
            ...getFeedbackDialogStoreLabels(),
            ...initialFeedbackState,
            clearFeedback,
            currentFeedbackType: 'request',
            isFeedbackActionsVisible: false,
            searchSkillsBySearchTerm: () => {
                const { currentFeedbackType, skillSearchInput } = getState();
                if (currentFeedbackType === 'request') {
                    return;
                }

                const searchTerm = skillSearchInput[currentFeedbackType].value;

                const isValidString = searchTerm.mapOr(false, (searchTerm) => searchTerm.trim().length >= 2);

                if (isValidString && searchTerm.isJust) {
                    feedbackEmitter.emit(fetchSkillsBySearchTermEvent, {
                        acceptLanguage: selectedLanguage,
                        searchTerm: searchTerm.value,
                    });
                }
            },
            sendFeedback: () => {
                const { currentFeedbackType, feedback } = getState();

                const currentFeedback = feedback[currentFeedbackType];

                if (currentFeedback.isJust) {
                    const { value } = currentFeedback;

                    const event = feedbackStateToEvent[currentFeedbackType];
                    const payload = transformFormStateToEventPayload(currentFeedbackType, value);

                    if (payload.isJust) {
                        feedbackEmitter.emit(event, payload.value);

                        clearFeedback();
                    }
                }
            },
            sendFeedbackToCobrainer: (message) => {
                mailTo({
                    body: message,
                    email: 'feedback@cobrainer.com',
                    subject: 'Feedback Cobrainer App',
                });
            },
            setFeedbackInformation: (information) => {
                const { currentFeedbackType, feedback, feedbackRaw } = getState();
                const currentFeedbackRaw = feedbackRaw[currentFeedbackType];
                const feedbackRawInput = {
                    ...currentFeedbackRaw,
                    ...information,
                };

                const currentFeedback = transformInputToFormState(currentFeedbackType, feedbackRawInput);

                set({
                    feedback: {
                        ...feedback,
                        [currentFeedbackType]: currentFeedback,
                    },
                    feedbackRaw: {
                        ...feedbackRaw,
                        [currentFeedbackType]: feedbackRawInput,
                    },
                });
            },
            setFeedbackType: (type) => set({ currentFeedbackType: type }),
            setSkillSearchInput: (value) => {
                const { currentFeedbackType, skillSearchInput } = getState();

                if (currentFeedbackType === 'request') {
                    return;
                }

                set({
                    skillSearchInput: {
                        ...skillSearchInput,
                        [currentFeedbackType]: {
                            ...skillSearchInput[currentFeedbackType],
                            value,
                        },
                    },
                });
            },
            skillSearchInput: {
                change: {
                    skills: Maybe.nothing(),
                    value: Maybe.nothing(),
                },
                report: {
                    skills: Maybe.nothing(),
                    value: Maybe.nothing(),
                },
            },
            toggleFeedbackActionsVisibility: () => {
                set((state) => ({
                    isFeedbackActionsVisible: state.isFeedbackActionsVisible === false,
                }));
            },
            updateStoreLabelsTranslations: () => {
                set({ ...getFeedbackDialogStoreLabels() });
            },
        };
    });

    return createStore<FeedbackDialogStoreWithComputedState>()(computedStore);
};
